import { Button, Chip, Typography } from '@components/base';
import css from './index.module.scss';
import { getFormattedDate, sanitizeValue } from '@helpers/utils';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuoteFormWrapper from '@components/common/quote-wrapper';
import notify from '@helpers/toastify-helper';
import {
  getEnquiryDetails,
  submitConsolidationQuote,
  submitNonConsolidationQuote
} from '@services/quote.service';
import { typographyVariantType } from '@helpers/types';
import SaveToDraftPrompt from '@components/common/quote-wrapper/components/save-to-draft-confirmation-modal';
import { MARKET_TYPE } from '@helpers/constants';
import { MultiplePlaceholder } from '@components/common';

const QuoteDraftCard = (props: any) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, fetchEnquiryList } = props;

  const productNames = useMemo(() => {
    if (!data?.product_details) return [];

    return data.product_details
      .map((product: any) => {
        const { product_data } = product;
        return product_data?.product_name;
      })
      .filter(Boolean); // Filter out any undefined or null values
  }, [data]);

  // Only include fields that have values
  const nonEmptyFields: {
    label: string;
    value: string | undefined;
    variant: typographyVariantType;
    isProductName?: boolean;
  }[] = [
    {
      label: 'Draft Enquiry Number',
      value: data?.readable_enquiry_number,
      variant: 'h5' as typographyVariantType
    },
    {
      label: 'Date',
      value: getFormattedDate(data?.created_at),
      variant: 'body' as typographyVariantType
    },
    {
      label: 'Product Name',
      value: productNames.length > 0 ? 'multiple' : undefined, // We'll handle this differently
      variant: 'body' as typographyVariantType,
      isProductName: true
    },
    {
      label: 'Deal Name',
      value: data?.deal_name,
      variant: 'body' as typographyVariantType
    }
  ].filter((field) => field.value !== undefined && field.value !== null && field.value !== '');

  const [draft, setDraft] = useState<any>({
    showQuoteForm: false,
    showDisclaimer: false,
    draftData: null,
    draftReqBody: null,
    consolidation: false
  });

  const { showQuoteForm, showDisclaimer, draftData, draftReqBody, consolidation } = draft;

  const onClose = (data: any) => {
    setDraft((prevState: any) => ({
      ...prevState,
      showDisclaimer: true,
      draftReqBody: data,
      consolidation: data?.is_consolidation
    }));
  };

  const fetchEnquiryDetail = async (enquiry_id: string) => {
    const response = await getEnquiryDetails(enquiry_id);
    if (response?.success) {
      const { data } = response;
      setDraft((prevState: any) => ({
        ...prevState,
        draftData: data
      }));
    } else if (response?.error) {
      notify({
        severity: 'error',
        message: 'Something Went Wrong' // response?.error?.message
      });
    }
  };

  const handleEditDraftEnquiry = (enquiryId: string) => {
    setDraft((prevState: any) => ({
      ...prevState,
      showQuoteForm: true
    }));
    fetchEnquiryDetail(enquiryId);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      showQuoteForm: 'true'
    });
  };

  const saveAsDraft = async () => {
    if (!draftReqBody) {
      notify({
        severity: 'error',
        message: 'No form data available to save'
      });
      return;
    }
    const response = consolidation
      ? await submitConsolidationQuote(draftReqBody)
      : await submitNonConsolidationQuote(draftReqBody);

    if (response.success) {
      notify({
        severity: 'success',
        message: response.data.message
      });
      setDraft((prevState: any) => ({
        ...prevState,
        showDisclaimer: false,
        showQuoteForm: false
      }));
      setSearchParams({
        ...Object.fromEntries(searchParams),
        showQuoteForm: 'false'
      });
      if (searchParams.get('activeTab') === 'cost_calculator') {
        fetchEnquiryList({
          is_draft: 'true'
        });
      } else {
        navigate('/quote?activeTab=cost_calculator');
      }
    } else {
      notify({
        severity: 'error',
        message: `Something Went Wrong!!`
      });
    }
  };

  return (
    <div className={css.mainWrapper}>
      <div className={css.text}>
        {nonEmptyFields.map((field, index) => (
          <div key={index} className={css.keyValue}>
            {field.label && <Typography variant="label">{field.label}</Typography>}
            {field.isProductName ? (
              <MultiplePlaceholder names={productNames} typographyVariant={field.variant} />
            ) : (
              <Typography variant={field.variant}>{sanitizeValue(field.value, '-')}</Typography>
            )}
          </div>
        ))}
      </div>
      <div className={css.buttons}>
        <Button
          variant="outlined-secondary"
          className={css.editButton}
          onClick={() => {
            handleEditDraftEnquiry(data?.enquiry_id);
          }}>
          Resume
        </Button>
        <Chip
          variant={data?.market_type === MARKET_TYPE.DOMESTIC ? 'filled-green' : 'filled-blue'}
          label={data?.market_type === MARKET_TYPE.DOMESTIC ? 'Domestic' : 'International'}
          className={css.marketBadge}
        />
      </div>

      {showQuoteForm && (
        <QuoteFormWrapper
          showQuoteForm={showQuoteForm}
          onClose={onClose}
          onSubmitClose={() => {
            setDraft((prevState: any) => ({
              ...prevState,
              showQuoteForm: false
            }));
            if (searchParams.get('activeTab') === 'cost_calculator') {
              fetchEnquiryList({
                is_draft: 'true'
              });
            } else {
              navigate('/quote?activeTab=cost_calculator');
            }
          }}
          data={draftData}
          isDraft={true}
        />
      )}
      {showDisclaimer && (
        <SaveToDraftPrompt
          open={showDisclaimer}
          onClose={() =>
            setDraft((prevState: any) => ({
              ...prevState,
              showDisclaimer: false
            }))
          }
          onConfirm={() => {
            setDraft((prevState: any) => ({
              ...prevState,
              showDisclaimer: false,
              showQuoteForm: false
            }));
            setSearchParams({
              ...Object.fromEntries(searchParams),
              showQuoteForm: 'false'
            });
          }}
          onSaveAsDraft={saveAsDraft}
          message="Are you sure you want to close?"
        />
      )}
    </div>
  );
};

export default QuoteDraftCard;
